import React from 'react';
import { useNavigate } from 'react-router-dom';
import Sheet from '../ui/Sheet';
import Icon from '../ui/Icon';
import Avatar from '../ui/Avatar';
import XPBar from '../player/XPBar';
import LevelBadge from '../player/LevelBadge';
import Streak from '../player/Streak';
import { useStudent } from './StudentContext';
import { usePopup } from '../../context/PopupContext';
import { clearAuth, getUser } from '../../utils/authUtil';

/**
 * Opened by tapping the avatar. Identity first (avatar, name, level, XP, streak — the real values from
 * /portal/player-profile), then the account actions. It is a menu, not a settings dashboard.
 */
export default function ProfileSheet() {
  const navigate = useNavigate();
  const { showConfirm } = usePopup();
  const user = getUser();
  const { player, unread, profileOpen, closeProfile } = useStudent();

  const go = (path) => { closeProfile(); navigate(path); };
  const logout = async () => {
    closeProfile();
    const ok = await showConfirm('Log out', 'Are you sure you want to log out of Zaptude?');
    if (ok) { clearAuth(); navigate('/login'); }
  };

  const rows = [
    { key: 'profile', icon: 'profile', title: 'Profile', sub: 'Achievements, skills and records', onClick: () => go('/student/profile') },
    { key: 'notifications', icon: 'notification', title: 'Notifications', sub: unread > 0 ? `${unread} unread` : 'You\'re all caught up', badge: unread, onClick: () => go('/student/notifications') },
    { key: 'feedback', icon: 'feedback', title: 'Feedback', sub: 'Report a bug or suggest an idea', onClick: () => go('/student/feedback') },
    { key: 'settings', icon: 'settings', title: 'Settings', sub: 'Password and preferences', onClick: () => go('/student/settings') },
  ];

  return (
    <Sheet open={profileOpen} onClose={closeProfile} label="Your profile menu" hideClose>
      <div className="z-profile-sheet">
        <div className="z-profile-id">
          <Avatar avatarId={player?.avatar_id} name={user?.name} size="lg" ring />
          <div className="z-profile-id-body">
            <div className="z-profile-name">{user?.name || 'Student'}</div>
            {player ? (
              <div className="z-profile-meta">
                <LevelBadge level={player.level} size="sm" />
                <span className="z-profile-xp">{player.xp.toLocaleString()} XP</span>
                <Streak streak={player.streak} compact />
              </div>
            ) : (
              <div className="z-profile-meta z-profile-meta--loading" aria-hidden="true" />
            )}
          </div>
        </div>
        {player && <XPBar levelProgress={player.levelProgress} />}

        <div className="z-list z-profile-list">
          {rows.map(r => (
            <button key={r.key} type="button" className="z-list-row" onClick={r.onClick}>
              <span className="z-profile-row-icon"><Icon name={r.icon} size={20} /></span>
              <span className="z-list-row-body">
                <span className="z-list-row-title">{r.title}</span>
                <span className="z-list-row-sub" style={{ display: 'block' }}>{r.sub}</span>
              </span>
              {r.badge > 0 && <span className="z-chip z-chip--violet">{r.badge}</span>}
              <Icon name="chevron-right" size={18} />
            </button>
          ))}
        </div>

        <button type="button" className="z-list-row z-list-row--danger z-profile-logout" onClick={logout}>
          <span className="z-profile-row-icon z-profile-row-icon--danger"><Icon name="logout" size={20} /></span>
          <span className="z-list-row-body"><span className="z-list-row-title">Log out</span></span>
        </button>
      </div>
    </Sheet>
  );
}
